import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { getcashbook } from "../../../../services/admin";
import moment from "moment";
import axios from "axios";
import baseUrl from "../../../../Api";
import { printReportUrl } from "../../../../utils/printPdf";

export default function CashBook() {
    const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
    const [cashbook, setCashbook] = useState([]);
    const [totals, setTotals] = useState({ receipts: 0, payments: 0, balance: 0 });
    const [members, setMembers] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchCashBook();
    }, []);

    const fetchMemberNames = async (memberIds) => {
        try {
            const uniqueIds = [...new Set(memberIds)];
            const memberData = {};

            for (const id of uniqueIds) {
                try {
                    const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/member/${id}`, {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    });
                    if (response.data.status === true && response.data.data.details) {
                        memberData[id] = response.data.data.details.member_name;
                    }
                } catch (err) {
                    memberData[id] = id; // Keep ID if fetch fails
                }
            }

            setMembers(memberData);
        } catch (error) {
            console.error("Error fetching member names:", error);
        }
    };

    const fetchCashBook = () => {
        setLoading(true);
        const data = {
            start_date: startDate,
            end_date: endDate,
            branch_id: localStorage.getItem("branch"),
        };

        getcashbook(data).then((res) => {
            if (res.status === true) {
                setCashbook(res.data.entries);
                setTotals({
                    receipts: res.data.total_receipts,
                    payments: res.data.total_payments,
                    balance: res.data.balance,
                });

                // Extract member IDs from narrations
                const memberIds = [];
                res.data.entries.forEach(entry => {
                    // Match patterns: "loan - 123" or "member 456" (with or without dash)
                    const match = entry.narration.match(/(?:loan\s*-\s*|member\s+)(\d+)/i);
                    if (match) {
                        memberIds.push(match[1]);
                    }
                });

                if (memberIds.length > 0) {
                    fetchMemberNames(memberIds);
                }
            }
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    };

    const formatNarration = (narration) => {
        // Handle "loan - 123" pattern
        let formatted = narration.replace(/loan\s*-\s*(\d+)/i, (match, id) => {
            return members[id] ? members[id] : match;
        });

        // Handle "member 456" pattern (without dash)
        formatted = formatted.replace(/member\s+(\d+)/i, (match, id) => {
            return members[id] ? members[id] : match;
        });

        return formatted;
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const columnDefs = [
        {
            headerName: "Date",
            field: "transaction_date",
            width: 140,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => moment(params.value).format("DD-MM-YYYY")
        },
        {
            headerName: "Type",
            field: "transaction_type",
            width: 140,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => {
                const isReceipt = params.value === "Receipt";
                const badgeClass = isReceipt ? 'bg-success' : 'bg-danger';
                return <span className={`badge ${badgeClass}`}>{params.value}</span>; 
                // `<span class="badge ${badgeClass}">${params.value}</span>`;
            }
        },
        {
            headerName: "Narration",
            field: "narration",
            width: 350,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => formatNarration(params.value)
        },
        {
            headerName: "Receipts (₹)",
            field: "receipt_amount",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: (params) => ({
                textAlign: "right",
                color: params.value > 0 ? '#198754' : ''
            }),
            cellRenderer: (params) => {
                return params.value > 0 ? formatCurrency(params.value) : '-';
            }
        },
        {
            headerName: "Payments (₹)",
            field: "payment_amount",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: (params) => ({
                textAlign: "right",
                color: params.value > 0 ? '#dc3545' : ''
            }),
            cellRenderer: (params) => {
                return params.value > 0 ? formatCurrency(params.value) : '-';
            }
        },
        {
            headerName: "Balance (₹)",
            field: "balance",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "right" },
            cellRenderer: (params) => formatCurrency(params.value)
        }
    ];

    const handleGenerate = () => {
        fetchCashBook();
    };

    const handleDownloadPDF = () => {
        const token = localStorage.getItem("token");
        const branchId = localStorage.getItem("branch");

        const url = `${baseUrl}/cashbook/pdf?start_date=${startDate}&end_date=${endDate}&branch_id=${branchId}`;

        fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
        .then(response => response.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `CashBook_${moment(startDate).format('DD-MM-YYYY')}_to_${moment(endDate).format('DD-MM-YYYY')}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        })
        .catch(error => {
            console.error('Error downloading PDF:', error);
            alert('Error downloading PDF');
        });
    };

    // Print sends the same PDF the download button produces straight to the printer.
    const handlePrint = () => {
        const token = localStorage.getItem("token");
        const branchId = localStorage.getItem("branch");

        printReportUrl(`${baseUrl}/cashbook/pdf?start_date=${startDate}&end_date=${endDate}&branch_id=${branchId}&output=html`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        });
    };

    const handleDownloadExcel = () => {
        const token = localStorage.getItem("token");
        const branchId = localStorage.getItem("branch");

        const url = `${baseUrl}/cashbook/excel?start_date=${startDate}&end_date=${endDate}&branch_id=${branchId}`;

        fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
        .then(response => response.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `CashBook_${moment(startDate).format('DD-MM-YYYY')}_to_${moment(endDate).format('DD-MM-YYYY')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        })
        .catch(error => {
            console.error('Error downloading Excel:', error);
            alert('Error downloading Excel');
        });
    };

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">Cash Book</h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Accounts</a>
                    </li>
                    <li className="breadcrumb-item">
                        <a>Reports</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        Cash Book
                    </li>
                </ol>
            </div>

            {loading && (
                <div className="loader-wrapper">
                    <div className="loader"></div>
                    <h3>Loading...Please do not go back or close the window.</h3>
                </div>
            )}

            {!loading && (
            <div className="content-body">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <h4 className="card-title">Cash Book</h4>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            {cashbook.length > 0 && (
                                                <>
                                                    <button className="btn btn-danger me-2" onClick={handleDownloadPDF}>
                                                        <i className="fa fa-file-pdf"></i> Download PDF
                                                    </button>
                                                    <button className="btn btn-success me-2" onClick={handleDownloadExcel}>
                                                        <i className="fa fa-file-excel"></i> Download Excel
                                                    </button>
                                                    <button className="btn btn-primary" onClick={handlePrint}>
                                                        <i className="fa fa-print"></i> Print
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                            <div className="row mb-4">
                                <div className="col-md-3">
                                    <label className="form-label">From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">To Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2 d-flex align-items-end">
                                    <button className="btn btn-primary" onClick={handleGenerate}>
                                        <i className="fa fa-search"></i> Generate
                                    </button>
                                </div>
                            </div>

                            {/* Cash Book Grid */}
                            <div className="ag-theme-alpine ag-style">
                                <AgGridReact
                                    columnDefs={columnDefs}
                                    rowData={cashbook}
                                    pagination={true}
                                    paginationPageSize={10}
                                    sortingOrder={["asc", "desc"]}
                                    suppressDragLeaveHidesColumns={true}
                                    domLayout="autoHeight"
                                />
                            </div>

                            {/* Totals Footer */}
                            {/* {cashbook.length > 0 && (
                                <div className="table-responsive mt-2">
                                    <table className="table table-bordered">
                                        <tbody>
                                            <tr className="table-secondary">
                                                <td width="12%"></td>
                                                <td width="15%"></td>
                                                <td width="33%" className="text-end"><strong>Total</strong></td>
                                                <td width="15%" className="text-end text-success">
                                                    <strong>{formatCurrency(totals.receipts)}</strong>
                                                </td>
                                                <td width="15%" className="text-end text-danger">
                                                    <strong>{formatCurrency(totals.payments)}</strong>
                                                </td>
                                                <td width="15%" className="text-end">
                                                    <strong>{formatCurrency(totals.balance)}</strong>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )} */}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            )}
        </div>
    );
}
